import {
  DeserializedRecord,
  deserializeItem,
  deserializeManyRelationship,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIManyRelationship,
  JSONAPIRecord,
  JSONAPISingleRelationship
} from "../Helpers";
import {deserializePeopleAssignments, PersonAssignment, Program} from "../Program";
import {deserializeSection, Section} from "./component/content/Section";
import {deserializeRestrictionIcd10, RestrictionIcd10} from "../restriction/icd10";
import {ComponentSurvey, deserializeComponentSurvey} from "./component/ComponentSurvey";
import {deserializeTask, Goal, Task} from "../Task";
import {Cohort} from "../Cohort";
import {deserializeVersion, Version} from "../Version";
import {deserializeProgramComponentMedicalBilling, ProgramComponentMedicalBilling} from "./component/MedicalBilling";

interface Component extends DeserializedRecord<"program_component"> {
  name: string;
  goal: string;
  targetGroup: string
  description?: string;
  durationInMinutes?: number;
  lastAuditedAt?: Date
  remark?: string;
  personAssignments: PersonAssignment[]
  sections: Section[]
  restrictionIcd10s: RestrictionIcd10[],
  programComponentSurveys: ComponentSurvey[],
  programComponentSymptomSurveys: ComponentSurvey[]
  programComponentLifestyleSurveys: ComponentSurvey[],
  tasks: Task[],
  goals: Task[];
  programComponentTags: ComponentTag[],
  tags: Cohort[]
  /**
   * The patient's tmhi admission is required, when a task template should be assigned, depending on the TMZ being PBA
   */
  tmhiAdmissionRequired: boolean;
  programComponentMedicalBillings: ProgramComponentMedicalBilling[];
  programs: Program[]
  versions: Version<Component>[];
  deletedAt?: Date;
}

interface ComponentData extends JSONAPIRecord {
  relationships: {
    person_assignments: JSONAPIManyRelationship,
    sections: JSONAPIManyRelationship,
    restriction_icd10s: JSONAPIManyRelationship,
    program_component_surveys: JSONAPIManyRelationship,
    tasks: JSONAPIManyRelationship,
    program_component_tags: JSONAPIManyRelationship,
    programs: JSONAPIManyRelationship,
    program_component_medical_billings: JSONAPIManyRelationship,
    versions: JSONAPIManyRelationship
  }
}

interface ComponentTag extends DeserializedRecord {
  tag: Cohort
}

interface ComponentTagData extends JSONAPIRecord {
  relationships: {
    tag: JSONAPISingleRelationship
  }
}

const deserializeProgramComponentTag: Deserializer<ComponentTag, ComponentTagData> = (data, included) => {
  const tag = deserializeSingleRelationship<Cohort>(data.relationships.tag, included);

  return {
    tag: tag,
    tagId: data.relationships.tag.data.id,
    ...deserializeItem(data)
  };
};

const deserializeProgramComponent: Deserializer<Component, ComponentData> = (data, included) => {
  const personAssignments = deserializeManyRelationship<PersonAssignment>(
    data.relationships.person_assignments,
    included,
    deserializePeopleAssignments
  );

  const programComponentTags = deserializeManyRelationship<ComponentTag>(
    data.relationships.program_component_tags,
    included,
    deserializeProgramComponentTag);

  const tags = programComponentTags.map((t) => t.tag);
  const sections = deserializeManyRelationship<Section>(data.relationships.sections, included, deserializeSection);
  const restrictionIcd10s = deserializeManyRelationship(data.relationships.restriction_icd10s, included, deserializeRestrictionIcd10);

  const programComponentSurveys = deserializeManyRelationship<ComponentSurvey>(
    data.relationships.program_component_surveys,
    included,
    deserializeComponentSurvey
  );

  const programComponentSymptomSurveys = programComponentSurveys
    .filter((componentSurvey) => componentSurvey?.survey?.category === 'symptom');

  const programComponentLifestyleSurveys = programComponentSurveys
    .filter((componentSurvey) => componentSurvey?.survey?.category === 'lifestyle');

  const tasks = deserializeManyRelationship<Task | Goal>(data.relationships.tasks, included, deserializeTask);
  const versions = deserializeManyRelationship(data.relationships.versions, included, deserializeVersion as Deserializer<Version<Component>>);

  const programComponentMedicalBillings = deserializeManyRelationship(
    data.relationships.program_component_medical_billings,
    included,
    deserializeProgramComponentMedicalBilling
  );

  return {
    personAssignments,
    sections,
    tasks,
    restrictionIcd10s,
    programComponentSurveys,
    programComponentSymptomSurveys,
    programComponentLifestyleSurveys,
    programComponentTags,
    tags,
    versions,
    programComponentMedicalBillings,
    ...deserializeItem(data)
  };
};

export {
  Component, ComponentData, deserializeProgramComponent, ComponentTag, deserializeProgramComponentTag
};
